import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Alert,
  App as AntdApp,
  Button,
  Divider,
  Form,
  Input,
  Typography,
} from 'antd'
import { KeyOutlined, LockOutlined, MailOutlined } from '@ant-design/icons'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import {
  ADMIN_EMAIL_DOMAIN,
  isAuthorizedAdmin,
  useAuthStore,
} from '@/auth/session'
import { redirectIfAuthed } from '@/auth/guards'

const searchSchema = z.object({ from: z.string().optional().catch(undefined) })

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: redirectIfAuthed,
  component: LoginPage,
})

function humanizeAuthError(raw: string): string {
  const m = raw.toLowerCase()
  if (
    m.includes('invalid login credentials') ||
    m.includes('invalid credentials')
  )
    return '邮箱或密码错误'
  if (m.includes('email not confirmed')) return '邮箱尚未验证'
  if (m.includes('too many requests') || m.includes('rate'))
    return '尝试过于频繁，请稍后再试'
  if (m.includes('failed to fetch') || m.includes('network'))
    return '网络连接失败，请检查网络后重试'
  return raw
}

// Supabase Custom OAuth Provider slug（配置在 Supabase Dashboard → Auth → Providers）。
// JWT 由 Supabase 签发，Custom Access Token Hook 仍负责注入 is_admin claim。
const PASSKEY_PROVIDER = 'custom:next-admin'

function LoginPage() {
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  async function onPasskeyLogin() {
    setSubmitError(null)
    setPasskeyLoading(true)
    const redirectTo = `${window.location.origin}/auth/callback`
    const { error } = await supabase.auth.signInWithOAuth({
      provider: PASSKEY_PROVIDER as never,
      options: { redirectTo, skipBrowserRedirect: false },
    })
    if (error) {
      setPasskeyLoading(false)
      setSubmitError(humanizeAuthError(error.message))
    }
    // 成功时浏览器会被 Supabase 跳到 Logto，无需处理后续。
  }

  return (
    <div className="login-shell">
      <aside className="login-brand" aria-label="稀饭动漫管理中心">
        <div className="login-brand__wordmark">XIFAN · ACG</div>
        <div className="login-brand__body">
          <h1 className="login-brand__title">稀饭动漫·管理中心</h1>
          <p className="login-brand__eyebrow">内部运营控制台</p>
        </div>
        <div className="login-brand__footer">
          <span className="login-brand__status-dot" aria-hidden="true" />
          <span>系统运行中</span>
          <span className="login-brand__sep" aria-hidden="true">
            ·
          </span>
          <span>v0.1</span>
        </div>
      </aside>

      <main className="login-form-wrap">
        <div className="login-form">
          <header className="login-form__header">
            <Typography.Title
              level={2}
              style={{ margin: 0, fontWeight: 600, fontSize: 22 }}
            >
              登录
            </Typography.Title>
          </header>

          {submitError ? (
            <Alert
              type="error"
              showIcon
              message={submitError}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <Button
            type="primary"
            size="large"
            block
            icon={<KeyOutlined aria-hidden="true" />}
            loading={passkeyLoading}
            onClick={onPasskeyLogin}
          >
            使用 稀饭 ACG 统一认证平台登录
          </Button>

          {/* 仅 `pnpm dev`：生产构建中 import.meta.env.DEV 为 false，整段被摇掉 */}
          {import.meta.env.DEV ? (
            <DevPasswordLogin
              disabled={passkeyLoading}
              onError={setSubmitError}
            />
          ) : null}

          <footer className="login-form__footer">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              无法登录？请联系系统管理员
            </Typography.Text>
          </footer>
        </div>
      </main>
    </div>
  )
}

interface PasswordFormValues {
  email: string
  password: string
}

/** 开发模式专用的邮箱密码登录；准入仍由 isAuthorizedAdmin（域名 + is_admin claim）把关。 */
function DevPasswordLogin({
  disabled,
  onError,
}: {
  disabled: boolean
  onError: (msg: string | null) => void
}) {
  const navigate = useNavigate()
  const { from } = Route.useSearch()
  const { message } = AntdApp.useApp()
  const [loading, setLoading] = useState(false)

  async function onFinish(values: PasswordFormValues) {
    onError(null)
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email.trim().toLowerCase(),
      password: values.password,
    })
    if (error) {
      setLoading(false)
      onError(humanizeAuthError(error.message))
      return
    }
    if (!isAuthorizedAdmin(useAuthStore.getState().claims)) {
      await supabase.auth.signOut()
      setLoading(false)
      onError('当前账号不是管理员，已退出登录')
      return
    }
    setLoading(false)
    message.success('登录成功')
    navigate({ to: from ?? '/dashboard', replace: true })
  }

  return (
    <>
      <Divider plain style={{ marginTop: 24, marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          开发模式 · 邮箱密码登录
        </Typography.Text>
      </Divider>
      <Form<PasswordFormValues>
        layout="vertical"
        onFinish={onFinish}
        autoComplete="on"
        requiredMark={false}
        disabled={loading || disabled}
        size="large"
      >
        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            { required: true, message: '请输入邮箱' },
            { type: 'email', message: '邮箱格式不正确' },
            {
              validator: (_, value: string) =>
                !value ||
                value.trim().toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(`仅允许 ${ADMIN_EMAIL_DOMAIN} 域账号`),
                    ),
            },
          ]}
        >
          <Input
            prefix={<MailOutlined aria-hidden="true" />}
            placeholder={`name${ADMIN_EMAIL_DOMAIN}`}
            autoComplete="email"
            spellCheck={false}
            inputMode="email"
          />
        </Form.Item>
        <Form.Item
          label="密码"
          name="password"
          rules={[{ required: true, message: '请输入密码' }]}
        >
          <Input.Password
            prefix={<LockOutlined aria-hidden="true" />}
            placeholder="密码"
            autoComplete="current-password"
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button htmlType="submit" loading={loading} block size="large">
            邮箱密码登录
          </Button>
        </Form.Item>
      </Form>
    </>
  )
}
